import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import recipes from '../data/recipes';
import styles from './RecipeDetailPage.module.css';

const DIFF_COLOR = { Easy: '#4caf87', Medium: '#f0a030', Hard: '#e05050' };
const DIFF_BG    = { Easy: 'rgba(76,175,135,0.12)', Medium: 'rgba(240,160,48,0.12)', Hard: 'rgba(224,80,80,0.12)' };

export default function RecipeDetailPage() {
  const { slug }   = useParams();
  const recipe     = recipes.find(r => r.slug === slug);
  const [done, setDone] = useState({});   // track checked steps
  const [servMul, setServMul] = useState(1); // serving multiplier

  if (!recipe) return <Navigate to="/baking" replace />;

  const { emoji, color, accentColor, difficulty, time, servings, date,
          category, title, description, tags, ingredients, steps, tips } = recipe;

  const toggleStep = i => setDone(d => ({ ...d, [i]: !d[i] }));

  const other = recipes.filter(r => r.category === category && r.id !== recipe.id).slice(0, 3);

  return (
    <div className={styles.root}>

      {/* ══════════════════════════════════════
          CINEMATIC HEADER
      ══════════════════════════════════════ */}
      <div className={styles.header}>
        {/* Colour wash bg */}
        <div className={styles.headerBg} style={{ background: color }} />
        <div className={styles.headerOverlay} />

        <div className="container">
          <Link to="/baking" className={styles.backBtn}>← Back to Recipes</Link>

          <div className={styles.headerInner}>
            {/* Giant emoji */}
            <div className={styles.emojiWrap}>
              <div className={styles.emojiGlow} style={{ background: accentColor }} />
              <span className={styles.heroEmoji}>{emoji}</span>
            </div>

            <div className={styles.headerContent}>
              {/* Meta row */}
              <div className={styles.metaRow}>
                <span
                  className={styles.diffBadge}
                  style={{ color: DIFF_COLOR[difficulty], background: DIFF_BG[difficulty], border: `1.5px solid ${DIFF_COLOR[difficulty]}40` }}
                >
                  {difficulty}
                </span>
                <span className={styles.catBadge} style={{ color: accentColor }}>
                  {category}
                </span>
                <span className={styles.metaItem}>⏱ {time}</span>
                {/* <span className={styles.metaItem}>🍽 {servings} servings</span> */}
                <span className={styles.metaItem}>📅 {date}</span>
              </div>

              <h1 className={styles.title}>{title}</h1>
              <p className={styles.desc}>{description}</p>

              <div className={styles.tagRow}>
                {tags.map(t => (
                  <span key={t} className={styles.tag}>#{t}</span>
                ))}
              </div>

              {/* Quick action bar */}
              <div className={styles.actionBar}>
                <button
                  className={styles.actionBtn}
                  onClick={() => navigator.share?.({ title, url: window.location.href })}
                >
                  📤 Share
                </button>
                <button
                  className={styles.actionBtn}
                  onClick={() => window.print()}
                >
                  🖨 Print
                </button>
                {/* <div className={styles.servingCtrl}>
                  <span className={styles.servingLabel}>Servings:</span>
                  <button
                    className={styles.servBtn}
                    onClick={() => setServMul(m => Math.max(0.5, m - 0.5))}
                  >−</button>
                  <span className={styles.servVal}>{servings * servMul}</span>
                  <button
                    className={styles.servBtn}
                    onClick={() => setServMul(m => m + 0.5)}
                  >+</button>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* wave */}
        <div className={styles.wave}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,30 C360,60 720,0 1080,30 C1260,45 1380,15 1440,25 L1440,60 L0,60 Z" fill="#fdf8fc"/>
          </svg>
        </div>
      </div>

      {/* ══════════════════════════════════════
          RECIPE BODY
      ══════════════════════════════════════ */}
      <div className="container">
        <div className={styles.body}>

          {/* ── INGREDIENTS ── */}
          <aside className={styles.ingredientsCol}>
            <div className={styles.stickyBox}>
              <h2 className={styles.colTitle}>
                <span className={styles.colTitleIcon}>🧂</span>
                Ingredients
              </h2>

              {servMul !== 1 && (
                <div className={styles.scaleBanner} style={{ borderColor: accentColor }}>
                  <span style={{ color: accentColor }}>✦</span>
                  Scaled to {servings * servMul} servings
                </div>
              )}

              <ul className={styles.ingList}>
                {ingredients.map((ing, i) => (
                  <li key={i} className={styles.ing}>
                    <span className={styles.ingBullet} style={{ background: accentColor }} />
                    <span className={styles.ingText}>{ing}</span>
                  </li>
                ))}
              </ul>

              {tips && (
                <div className={styles.tipBox} style={{ borderColor: accentColor }}>
                  <p className={styles.tipTitle} style={{ color: accentColor }}>💡 Baker's tip</p>
                  <p className={styles.tipText}>{tips}</p>
                </div>
              )}

              {/* Difficulty at a glance */}
              <div className={styles.glanceGrid}>
                {[
                  { icon:'⏱', label:'Total time',  val: time },
                  { icon:'🍽', label:'Servings',    val: `${servings * servMul}` },
                  { icon:'📊', label:'Difficulty',  val: difficulty },
                  { icon:'📂', label:'Category',    val: category },
                ].map(({ icon, label, val }) => (
                  <div key={label} className={styles.glanceCard}>
                    <span className={styles.glanceIcon}>{icon}</span>
                    <span className={styles.glanceLabel}>{label}</span>
                    <span className={styles.glanceVal}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* ── METHOD ── */}
          <div className={styles.methodCol}>
            <h2 className={styles.colTitle}>
              <span className={styles.colTitleIcon}>📋</span>
              Method
            </h2>

            <div className={styles.steps}>
              {steps.map((step, i) => (
                <div
                  key={i}
                  className={`${styles.step} ${done[i] ? styles.stepDone : ''}`}
                  onClick={() => toggleStep(i)}
                >
                  <div
                    className={styles.stepNum}
                    style={{ background: done[i] ? '#4caf87' : accentColor }}
                  >
                    {done[i] ? '✓' : i + 1}
                  </div>
                  <div className={styles.stepRight}>
                    <p className={styles.stepText}>{step}</p>
                    {done[i] && <span className={styles.stepDoneLabel}>Done ✓</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className={styles.progressWrap}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{
                    width: `${(Object.values(done).filter(Boolean).length / steps.length) * 100}%`,
                    background: accentColor,
                  }}
                />
              </div>
              <span className={styles.progressLabel}>
                {Object.values(done).filter(Boolean).length} / {steps.length} steps
                {Object.values(done).filter(Boolean).length === steps.length && ' 🎉 All done!'}
              </span>
            </div>
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════
          MORE FROM THIS CATEGORY
      ══════════════════════════════════════ */}
      {other.length > 0 && (
        <section className={styles.moreSection}>
          <div className="container">
            <div className={styles.moreHead}>
              <h2 className={styles.moreTitle}>More {category}</h2>
              <Link to="/baking" className={styles.moreCta}>See all recipes →</Link>
            </div>
            <div className={styles.moreGrid}>
              {other.map(r => (
                <Link key={r.id} to={`/baking/${r.slug}`} className={styles.moreCard}>
                  <div className={styles.moreCardTop} style={{ background: r.color }}>
                    <span className={styles.moreEmoji}>{r.emoji}</span>
                  </div>
                  <div className={styles.moreCardBody}>
                    <span className={styles.moreCat} style={{ color: r.accentColor }}>{r.category}</span>
                    <h3 className={styles.moreCardTitle}>{r.title}</h3>
                    <span className={styles.moreTime}>⏱ {r.time}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

    </div>
  );
}
